import { isEntity } from '@tunasong/schemas'
import type { CoreElement, Entity } from '@tunasong/schemas'
import { entityTemplate } from '../element/templates.js'

export const noteTypes = ['general', 'performance', 'instrument'] as const
export type NoteType = (typeof noteTypes)[number]

export interface Note extends Entity, Omit<CoreElement, 'children'> {
  type: 'note'
  /**
   * Parent entity this note is scoped to. OPTIONAL (GH #1579): notes are regular
   * standalone entities — a note may have no parent (e.g. captured to the mobile
   * Inbox). When set, the note view surfaces a backlink to the parent rather than
   * being strictly nested under it. Mirrors the Zod schema, where `parentId` is
   * inherited as optional from `baseEntitySchema`.
   */
  parentId?: string
  noteType?: NoteType
  /**
   * Optional element id this note is anchored to within the parent song.
   * When set, the note is rendered with an inline marker on the matching Slate
   * element; when unset, the note is entity-level only.
   */
  targetElementId?: string
}

export const noteTemplate = ({
  parentId,
  noteType = 'general',
  id,
  userId,
  targetElementId,
}: {
  parentId?: string
  noteType?: NoteType
  id?: string
  userId?: string
  targetElementId?: string
}): Note => ({
  // Notes are YDoc-backed: their content lives in `yDoc` (S3 + inline blob),
  // never in `children`. The `Note` interface intentionally omits `children`
  // (`Omit<CoreElement, 'children'>`); the schema kept it `optional().nullable()`
  // only for legacy notes created before the YDoc migration.
  ...entityTemplate,
  id,
  type: 'note',
  parentId,
  noteType,
  targetElementId,
  userId,
  updatedAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
})

export function isNote(node?: unknown): node is Note {
  return Boolean(isEntity(node) && node.type === 'note')
}
